import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { useApiFetch } from '../../shared/api'
import type { HuntPlanDetail } from '@wehunt/ui'

export interface UpcomingHuntDetailResponse {
  id: string
  huntName: string
  groupId: string
  groupName: string
  startTime: string
  endTime: string | null
  huntableGames: { species: string; classes: { name: string; limit: number | null }[] }[]
  participants: {
    userId: string
    userName: string
    isLeader: boolean
    rsvpStatus: string | null
    responseText: string | null
    joinDate?: string | null
    leaveDate?: string | null
    /** The email/phone the organiser invited this guest with (CLUB-1080); absent
     *  for a team member. Shown below the name once the guest has joined. */
    invitedEmail?: string | null
    invitedPhone?: string | null
  }[]
  drives: {
    name: string
    standCount: number
    stands: { id: string; assignedUserId: string | null; name?: string | null }[]
  }[]
  invitation: string | null
  joinCode: string | null
  isOrganizer: boolean
  invitedBy: string | null
  inviteStatus: string | null
  attachmentFiles?: {
    fileName: string
    storagePath: string
    mimeType: string | null
    sizeBytes: number | null
  }[]
  /** Ids of documents attached from the team's documents, not uploaded copies. */
  attachmentDocumentIds?: string[]
  comment?: string | null
  dogs?: {
    id: string
    name: string
    ownerName?: string | null
    colorArgb?: number | null
    icon?: string | null
  }[]
  manHours?: number
  /** Whether the plan's hunt has already been started (CLUB-883). */
  started?: boolean
}

const toUiRsvp = (s: string | null): string | null => (s === 'rejected' ? 'declined' : s)

function responseToDetail(r: UpcomingHuntDetailResponse): HuntPlanDetail {
  return {
    id: r.id,
    huntName: r.huntName,
    groupId: r.groupId,
    groupName: r.groupName,
    startTime: r.startTime,
    endTime: r.endTime,
    // Species names keep the existing string[] contract (quarry summary etc.);
    // the structured details carry classes + per-type limit for the game cards.
    huntableGames: r.huntableGames.map((g) => g.species),
    huntableGameDetails: r.huntableGames.map((g) => ({
      species: g.species,
      // Tolerate a missing/null classes array defensively — a game with no
      // chosen classes must map to an empty list, never throw.
      classes: (g.classes ?? []).map((c) => ({ name: c.name, limit: c.limit })),
    })),
    participants: r.participants.map((p) => ({
      userId: p.userId,
      userName: p.userName,
      isLeader: p.isLeader,
      rsvpStatus: toUiRsvp(p.rsvpStatus),
      responseText: p.responseText ?? null,
      joinDate: p.joinDate ?? null,
      leaveDate: p.leaveDate ?? null,
      invitedEmail: p.invitedEmail ?? null,
      invitedPhone: p.invitedPhone ?? null,
    })),
    drives: r.drives.map((d) => ({
      name: d.name,
      standCount: d.standCount,
      stands: (d.stands ?? []).map((s) => ({
        id: s.id,
        assignedUserId: s.assignedUserId ?? null,
        name: s.name ?? null,
      })),
    })),
    invitation: r.invitation,
    joinCode: r.joinCode,
    isOrganizer: r.isOrganizer,
    invitedBy: r.invitedBy,
    inviteStatus: toUiRsvp(r.inviteStatus),
    attachmentFiles: (r.attachmentFiles ?? []).map((f) => ({
      fileName: f.fileName,
      storagePath: f.storagePath,
      mimeType: f.mimeType,
      sizeBytes: f.sizeBytes,
    })),
    attachmentDocumentIds: r.attachmentDocumentIds ?? [],
    comment: r.comment ?? null,
    // Tolerated as absent so the web can deploy ahead of the API that adds these,
    // rather than rendering undefined in a tile.
    dogs: (r.dogs ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      ownerName: d.ownerName ?? null,
      colorArgb: d.colorArgb ?? null,
      icon: d.icon ?? null,
    })),
    manHours: r.manHours ?? 0,
    started: r.started ?? false,
  }
}

export function useUpcomingHuntDetail(groupId: string | undefined, huntId: string | undefined) {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useQuery({
    queryKey: ['clubhouse', 'groups', groupId, 'hunts', huntId, 'detail', uid],
    queryFn: async () => {
      const r = await call<UpcomingHuntDetailResponse>(
        `/clubhouse/groups/${groupId}/hunts/${huntId}`,
      )
      return responseToDetail(r)
    },
    enabled: !!uid && !!groupId && !!huntId,
    staleTime: 60 * 1000,
  })
}
